import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";

// eslint-disable-next-line static-service/require-shared-package-imports-only -- TODO: Port video package to libs
import {validatePlaybackRate} from "~/video-package/playback-rate-helpers.ts";

import type {PlaybackRate} from "../types/account-settings-types.ts";

interface Props {
    muteVideos: boolean;
    showCaptions: boolean;
    playbackRate: PlaybackRate;
    disabled: boolean;
    onChangeMuteVideos: (muteVideos: boolean) => void;
    onChangeShowCaptions: (showCaptions: boolean) => void;
    onChangePlaybackRate: (playbackRate: PlaybackRate) => void;
}

export const StudentVideoSettings: React.FC<Props> = ({
    muteVideos,
    showCaptions,
    playbackRate,
    disabled,
    onChangeMuteVideos,
    onChangeShowCaptions,
    onChangePlaybackRate,
}) => {
    // maps from the graphql enum value to the display string
    const playbackRateItems = [
        {value: "RATE_25_PERCENT", label: t`.25`},
        {value: "RATE_50_PERCENT", label: t`.50`},
        {value: "RATE_75_PERCENT", label: t`.75`},
        {value: "RATE_100_PERCENT", label: t`Normal`},
        {value: "RATE_125_PERCENT", label: t`1.25`},
        {value: "RATE_150_PERCENT", label: t`1.5`},
        {value: "RATE_175_PERCENT", label: t`1.75`},
        {value: "RATE_200_PERCENT", label: t`2`},
    ];

    const playbackRateValue = playbackRate || "RATE_100_PERCENT";

    const handlePlaybackRateChange = (inputValue: string) => {
        const validatedPlaybackRate = validatePlaybackRate(inputValue);
        if (validatedPlaybackRate != null) {
            onChangePlaybackRate(validatedPlaybackRate);
        }
    };

    return (
        <View style={styles.container}>
            <View style={{gap: sizing.size_120}}>
                <HeadingSmall tag="h2">{t`Video`}</HeadingSmall>
                <Body>{t`Customize how videos play during your lessons.`}</Body>
            </View>
            <View style={{marginBlockStart: sizing.size_240}} />
            <Checkbox
                label={t`Mute videos`}
                id="mute-videos"
                checked={muteVideos}
                onChange={onChangeMuteVideos}
                disabled={disabled}
            />
            <View style={{marginBlockStart: sizing.size_240}} />
            <Checkbox
                label={t`Show captions`}
                id="show-captions"
                checked={showCaptions}
                onChange={onChangeShowCaptions}
                disabled={disabled}
            />
            <View style={{marginBlockStart: sizing.size_240}} />
            <LabeledField
                label={t`Playback speed`}
                field={
                    <SingleSelect
                        aria-label={t`Playback speed`}
                        onChange={handlePlaybackRateChange}
                        id="playback_rate_label"
                        placeholder={t`Playback speed`}
                        selectedValue={playbackRateValue}
                        disabled={disabled}
                        style={styles.playbackSpeedDropdown}
                    >
                        {playbackRateItems.map((rate) => (
                            <OptionItem
                                label={rate.label}
                                value={rate.value}
                                testId={rate.value}
                                key={rate.value}
                            />
                        ))}
                    </SingleSelect>
                }
            />
        </View>
    );
};

export default React.memo(StudentVideoSettings);

const styles = StyleSheet.create({
    container: {
        maxWidth: 400,
        width: "100%",
    },
    playbackSpeedDropdown: {
        width: "100%",
    },
});
